// src/components/EnOcean/ButtonDiscoveryDialog.jsx
// Dialog for discovering and registering new EnOcean buttons
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  LinearProgress,
  Divider,
  Paper,
  Tooltip,
  useTheme,
  useMediaQuery,
  Collapse,
  Autocomplete
} from '@mui/material';
import {
  PlayArrow as StartIcon,
  Stop as StopIcon,
  RadioButtonChecked as ButtonIcon,
  SignalCellularAlt as SignalIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  TouchApp as TouchAppIcon,
  Delete as DeleteIcon,
  Place as PlaceIcon,
  DirectionsWalk as MobileIcon,
  Router as HubIcon
} from '@mui/icons-material';
import enoceanService from '../../services/enoceanService';

// Button type options
const BUTTON_TYPES = [
  { value: 'emergency', label: 'Emergency Button', color: 'error' },
  { value: 'action', label: 'Action Button', color: 'primary' },
  { value: 'doorbell', label: 'Doorbell', color: 'info' },
  { value: 'other', label: 'Other', color: 'default' }
];

// Signal quality indicators
const getSignalInfo = (rssiDbm) => {
  if (rssiDbm === null || rssiDbm === undefined) {
    return { label: 'Unknown', color: 'default', icon: null };
  }
  if (rssiDbm >= -50) return { label: 'Excellent', color: 'success', bars: 4 };
  if (rssiDbm >= -60) return { label: 'Good', color: 'success', bars: 3 };
  if (rssiDbm >= -70) return { label: 'Fair', color: 'warning', bars: 2 };
  if (rssiDbm >= -80) return { label: 'Poor', color: 'error', bars: 1 };
  return { label: 'Very Poor', color: 'error', bars: 0 };
};

// Helper to get parent ID from location (handles various field names)
const getParentId = (location) => {
  return location?.parent || location?.parentId || location?.parent_id || location?.parentLocation || null;
};

// Helper to find all locations in the same site as a given location
const getLocationsInSameSite = (locationId, allLocations) => {
  if (!locationId || !allLocations || allLocations.length === 0) return [];

  // Find the root site for this location by traversing up the parent chain
  const findRootSite = (locId) => {
    const location = allLocations.find(l => l.id === locId);
    if (!location) return null;

    const parentId = getParentId(location);
    // If no parent or parent is 0/null/1 (typically root), this is the site
    if (!parentId || parentId === 0 || parentId === 1) {
      return location;
    }

    // Traverse up to parent
    return findRootSite(parentId);
  };

  const rootSite = findRootSite(locationId);
  if (!rootSite) return allLocations; // Fallback to all if can't find site

  // Find all descendants of this root site
  const getDescendants = (parentId, collected = new Set()) => {
    collected.add(parentId);
    const children = allLocations.filter(l => getParentId(l) === parentId);
    children.forEach(child => {
      getDescendants(child.id, collected);
    });
    return collected;
  };

  const siteLocationIds = getDescendants(rootSite.id);
  return allLocations.filter(l => siteLocationIds.has(l.id));
};

const ButtonDiscoveryDialog = ({
  open,
  onClose,
  hubs = [],
  locations = [],
  persons = [],
  onButtonRegistered
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Hub selection state
  const [selectedHub, setSelectedHub] = useState(null);

  // Learning state
  const [isLearning, setIsLearning] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [remainingTime, setRemainingTime] = useState(0);

  // Discovered buttons
  const [discoveredButtons, setDiscoveredButtons] = useState([]);
  const [selectedButton, setSelectedButton] = useState(null);

  // Configuration form
  const [assignmentType, setAssignmentType] = useState(null); // 'fixed' or 'mobile'
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [selectedPersonId, setSelectedPersonId] = useState(null);
  const [friendlyName, setFriendlyName] = useState('');
  const [buttonType, setButtonType] = useState('action');

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeStep, setActiveStep] = useState(0);

  // Person mobile button check
  const [personButtonWarning, setPersonButtonWarning] = useState(null);
  const [checkingPerson, setCheckingPerson] = useState(false);

  // Site-specific data loaded from API
  const [siteLocations, setSiteLocations] = useState([]);
  const [sitePersons, setSitePersons] = useState([]);
  const [loadingSiteData, setLoadingSiteData] = useState(false);

  // Enrich hubs with location names for search
  const enrichedHubs = useMemo(() => {
    if (!hubs || !Array.isArray(hubs)) return [];
    return hubs.filter(hub => hub != null).map(hub => {
      const hubLocation = locations?.find(loc => loc?.id === hub.deviceLocation);
      const locName = hubLocation?.friendlyName || hubLocation?.friendly_name || hub.location?.friendlyName || '';
      return {
        ...hub,
        locationName: locName,
        searchLabel: `${hub.friendlyName || 'Unknown Hub'}${locName ? ` - ${locName}` : ''}`
      };
    });
  }, [hubs, locations]);

  // Load site-specific locations and persons when hub is selected
  useEffect(() => {
    const loadSiteData = async () => {
      if (!selectedHub?.id) {
        setSiteLocations([]);
        setSitePersons([]);
        return;
      }

      setLoadingSiteData(true);
      try {
        const response = await enoceanService.getHubSiteData(selectedHub.id);
        if (response?.success && response?.data) {
          const locs = response.data.locations;
          const pers = response.data.persons;
          setSiteLocations(Array.isArray(locs) ? locs.filter(l => l != null) : []);
          setSitePersons(Array.isArray(pers) ? pers.filter(p => p != null) : []);
        } else {
          // Fallback to passed props if API fails
          setSiteLocations(Array.isArray(locations) ? locations : []);
          setSitePersons(Array.isArray(persons) ? persons.filter(p => p?.personType == 1) : []); // eslint-disable-line eqeqeq
        }
      } catch (err) {
        console.error('Error loading site data:', err);
        // Fallback to passed props
        setSiteLocations(Array.isArray(locations) ? locations : []);
        setSitePersons(Array.isArray(persons) ? persons.filter(p => p?.personType == 1) : []); // eslint-disable-line eqeqeq
      } finally {
        setLoadingSiteData(false);
      }
    };

    loadSiteData();
  }, [selectedHub, locations, persons]);

  // Enrich site locations with search labels
  const enrichedLocations = useMemo(() => {
    if (!siteLocations || !Array.isArray(siteLocations)) return [];
    return siteLocations.filter(loc => loc != null).map(loc => {
      // Build path from parent chain
      const buildPath = (location) => {
        if (!location) return '';
        const parentId = getParentId(location);
        if (!parentId || parentId === 0 || parentId === 1) {
          return location.friendlyName || location.friendly_name || '';
        }
        const parent = siteLocations.find(l => l?.id === parentId);
        if (parent) {
          return `${buildPath(parent)} > ${location.friendlyName || location.friendly_name || ''}`;
        }
        return location.friendlyName || location.friendly_name || '';
      };

      const name = loc.friendlyName || loc.friendly_name || `Location ${loc.id}`;
      const path = buildPath(loc);
      return {
        ...loc,
        displayName: name,
        path: path,
        searchLabel: `${name} ${path}`
      };
    });
  }, [siteLocations]);

  // Enrich site persons with location names for search
  const filteredPersons = useMemo(() => {
    if (!sitePersons || !Array.isArray(sitePersons)) return [];
    return sitePersons.filter(person => person != null).map(person => ({
      ...person,
      locationName: person.locationName || '',
      searchLabel: `${person.friendlyName || 'Unknown'}${person.locationName ? ` - ${person.locationName}` : ''}`
    }));
  }, [sitePersons]);

  // Check if selected person already has a mobile button
  useEffect(() => {
    const checkPersonButton = async () => {
      if (!selectedPersonId) {
        setPersonButtonWarning(null);
        return;
      }

      setCheckingPerson(true);
      try {
        const result = await enoceanService.checkPersonMobileButton(selectedPersonId);
        if (result.success && result.data.hasButton) {
          setPersonButtonWarning({
            personId: selectedPersonId,
            existingButton: result.data.button
          });
        } else {
          setPersonButtonWarning(null);
        }
      } catch (err) {
        console.error('Error checking person button:', err);
        setPersonButtonWarning(null);
      } finally {
        setCheckingPerson(false);
      }
    };

    checkPersonButton();
  }, [selectedPersonId]);

  // Countdown timer
  useEffect(() => {
    if (!expiresAt) {
      setRemainingTime(0);
      return;
    }

    const updateRemaining = () => {
      const remaining = Math.max(0, new Date(expiresAt) - new Date());
      setRemainingTime(remaining);

      if (remaining <= 0) {
        setIsLearning(false);
        setSessionId(null);
        setExpiresAt(null);
      }
    };

    updateRemaining();
    const interval = setInterval(updateRemaining, 1000);
    return () => clearInterval(interval);
  }, [expiresAt]);

  // WebSocket event handlers
  const handleButtonDiscovered = useCallback((data) => {
    if (!selectedHub || data.hubDeviceId !== selectedHub.id) return;

    const normalizedData = {
      ...data,
      id: data.id || data.discoveryId,
      discoveryId: data.discoveryId || data.id
    };

    setDiscoveredButtons(prev => {
      const exists = prev.find(b => b.hexCode === normalizedData.hexCode);
      if (exists) {
        return prev.map(b =>
          b.hexCode === normalizedData.hexCode
            ? { ...b, ...normalizedData, updatedAt: new Date().toISOString() }
            : b
        );
      }
      return [...prev, { ...normalizedData, status: 'discovered' }];
    });

    setSuccess(`New button discovered: ${normalizedData.hexCode}`);
    setTimeout(() => setSuccess(null), 3000);
  }, [selectedHub]);

  const handleAwaitingConfirmation = useCallback((data) => {
    if (!selectedHub || data.hubDeviceId !== selectedHub.id) return;

    setDiscoveredButtons(prev =>
      prev.map(b =>
        b.hexCode === data.hexCode
          ? { ...b, status: 'awaiting_confirmation' }
          : b
      )
    );
    setActiveStep(2);
  }, [selectedHub]);

  const handleButtonConfirmed = useCallback((data) => {
    if (!selectedHub || data.hubDeviceId !== selectedHub.id) return;

    setDiscoveredButtons(prev =>
      prev.map(b =>
        b.hexCode === data.hexCode
          ? { ...b, status: 'confirmed', deviceId: data.deviceId }
          : b
      )
    );

    setSuccess(`Button ${data.hexCode} successfully registered!`);
    setActiveStep(3);

    if (onButtonRegistered) {
      onButtonRegistered(data);
    }
  }, [selectedHub, onButtonRegistered]);

  const handleLearningStopped = useCallback((data) => {
    if (!selectedHub || data.hubDeviceId !== selectedHub.id) return;

    setIsLearning(false);
    setSessionId(null);
    setExpiresAt(null);

    if (data.reason === 'timeout') {
      setError('Learning mode timed out');
    }
  }, [selectedHub]);

  // Initialize WebSocket and subscribe to hub events
  useEffect(() => {
    if (!open || !selectedHub?.id) return;

    const unsubscribe = enoceanService.subscribeToHub(selectedHub.id, {
      onDiscovered: handleButtonDiscovered,
      onAwaitingConfirmation: handleAwaitingConfirmation,
      onConfirmed: handleButtonConfirmed,
      onLearningStopped: handleLearningStopped
    });

    // Fetch current learning status
    fetchLearningStatus();

    return () => {
      unsubscribe();
    };
  }, [open, selectedHub, handleButtonDiscovered, handleAwaitingConfirmation, handleButtonConfirmed, handleLearningStopped]);

  // Fetch learning status
  const fetchLearningStatus = async () => {
    if (!selectedHub?.id) return;

    try {
      const response = await enoceanService.getLearningStatus(selectedHub.id);
      if (response.success && response.data.isLearning) {
        setIsLearning(true);
        setSessionId(response.data.sessionId);
        setExpiresAt(response.data.expiresAt);
        const normalizedButtons = (response.data.discoveredButtons || []).map(button => ({
          ...button,
          id: button.id || button.discoveryId,
          discoveryId: button.discoveryId || button.id
        }));
        setDiscoveredButtons(normalizedButtons);
      }
    } catch (err) {
      console.error('Error fetching learning status:', err);
    }
  };

  // Start learning mode
  const handleStartLearning = async () => {
    if (!selectedHub?.id) {
      setError('Please select a hub first');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await enoceanService.startLearning(selectedHub.id);
      if (response.success) {
        setIsLearning(true);
        setSessionId(response.data.sessionId);
        setExpiresAt(response.data.expiresAt);
        setDiscoveredButtons([]);
        setActiveStep(0);
        setSuccess('Learning mode started. Press any EnOcean button to discover it.');
        setTimeout(() => setSuccess(null), 5000);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start learning mode');
    } finally {
      setLoading(false);
    }
  };

  // Stop learning mode
  const handleStopLearning = async () => {
    if (!selectedHub?.id) return;

    setLoading(true);
    setError(null);

    try {
      await enoceanService.stopLearning(selectedHub.id);
      setIsLearning(false);
      setSessionId(null);
      setExpiresAt(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to stop learning mode');
    } finally {
      setLoading(false);
    }
  };

  // Select a discovered button for configuration
  const handleSelectButton = (button) => {
    setSelectedButton(button);
    setFriendlyName(`EnOcean Button ${button.hexCode}`);
    setActiveStep(1);
  };

  // Configure and submit button
  const handleConfigureButton = async () => {
    if (!selectedButton) {
      setError('Please select a button');
      return;
    }

    if (assignmentType === 'fixed' && !selectedLocationId) {
      setError('Please select a location for fixed button');
      return;
    }

    if (assignmentType === 'mobile' && !selectedPersonId) {
      setError('Please select a person for mobile button');
      return;
    }

    if (!assignmentType) {
      setError('Please select whether this button is fixed or mobile');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const buttonDiscoveryId = selectedButton.discoveryId || selectedButton.id || selectedButton.hexCode;
      const response = await enoceanService.configureButton(buttonDiscoveryId, {
        locationId: assignmentType === 'fixed' ? selectedLocationId : null,
        personId: assignmentType === 'mobile' ? selectedPersonId : null,
        friendlyName: friendlyName || `EnOcean Button ${selectedButton.hexCode}`,
        buttonType
      });

      if (response.success) {
        setSuccess('Configuration saved. Press the button again to confirm registration.');
        setActiveStep(2);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to configure button');
    } finally {
      setLoading(false);
    }
  };

  // Cancel discovery
  const handleCancelDiscovery = async (buttonIdentifier) => {
    try {
      await enoceanService.cancelDiscovery(buttonIdentifier);
      setDiscoveredButtons(prev => prev.filter(b =>
        (b.id || b.discoveryId || b.hexCode) !== buttonIdentifier
      ));
      const selectedId = selectedButton?.id || selectedButton?.discoveryId || selectedButton?.hexCode;
      if (selectedId === buttonIdentifier) {
        setSelectedButton(null);
        setActiveStep(0);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to cancel discovery');
    }
  };

  // Reset form
  const handleReset = () => {
    setSelectedButton(null);
    setAssignmentType(null);
    setSelectedLocationId(null);
    setSelectedPersonId(null);
    setFriendlyName('');
    setButtonType('action');
    setActiveStep(0);
    setError(null);
    setSuccess(null);
  };

  // Full reset including hub
  const handleFullReset = () => {
    handleReset();
    setSelectedHub(null);
    setDiscoveredButtons([]);
    setIsLearning(false);
    setSessionId(null);
    setExpiresAt(null);
  };

  // Close dialog
  const handleClose = () => {
    if (isLearning) {
      handleStopLearning();
    }
    handleFullReset();
    onClose();
  };

  // Format remaining time
  const formatTime = (ms) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <ButtonIcon color="primary" />
            <Typography variant="h6">
              Button Discovery
            </Typography>
          </Box>
          {isLearning && (
            <Chip
              icon={<SignalIcon />}
              label={`Learning: ${formatTime(remainingTime)}`}
              color="success"
              size="small"
            />
          )}
        </Box>
        {selectedHub && (
          <Typography variant="body2" color="text.secondary">
            Hub: {selectedHub.friendlyName}
            {selectedHub.locationName && ` (${selectedHub.locationName})`}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent dividers>
        {/* Error/Success Messages */}
        <Collapse in={!!error}>
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        </Collapse>
        <Collapse in={!!success}>
          <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
            {success}
          </Alert>
        </Collapse>

        {/* Step 1: Hub Selection */}
        {!selectedHub && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Step 1: Select Hub
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Choose the hub that is closest to the button you want to discover.
              Locations and persons will be filtered based on the hub's site.
            </Typography>

            <Autocomplete
              options={enrichedHubs}
              getOptionLabel={(option) => option.searchLabel || option.friendlyName || ''}
              value={selectedHub}
              onChange={(e, newValue) => {
                setSelectedHub(newValue);
                // Reset location and person when hub changes
                setSelectedLocationId(null);
                setSelectedPersonId(null);
              }}
              filterOptions={(options, { inputValue }) => {
                const searchTerm = inputValue.toLowerCase();
                return options.filter(option =>
                  option.friendlyName?.toLowerCase().includes(searchTerm) ||
                  option.locationName?.toLowerCase().includes(searchTerm)
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search Hub"
                  placeholder="Type to search by hub name or location..."
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <HubIcon fontSize="small" color="primary" />
                      <Typography variant="body2">{option.friendlyName}</Typography>
                    </Box>
                    {option.locationName && (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 3.5 }}>
                        <LocationIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                        {option.locationName}
                      </Typography>
                    )}
                  </Box>
                </li>
              )}
              ListboxProps={{ style: { maxHeight: 400 } }}
              noOptionsText="No hubs found"
              isOptionEqualToValue={(option, value) => option.id === value?.id}
            />

            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {enrichedHubs.length} hub{enrichedHubs.length !== 1 ? 's' : ''} available
            </Typography>
          </Box>
        )}

        {/* Learning Mode Progress */}
        {selectedHub && isLearning && (
          <Box sx={{ mb: 3 }}>
            <LinearProgress
              variant="determinate"
              value={(remainingTime / (10 * 60 * 1000)) * 100}
              sx={{ mb: 1 }}
            />
            <Typography variant="caption" color="text.secondary">
              Learning mode will automatically stop in {formatTime(remainingTime)}
            </Typography>
          </Box>
        )}

        {/* Start/Stop Learning */}
        {selectedHub && !isLearning && discoveredButtons.length === 0 && (
          <Paper
            elevation={0}
            sx={{
              p: 3,
              textAlign: 'center',
              bgcolor: 'action.hover',
              borderRadius: 2,
              mb: 2
            }}
          >
            <TouchAppIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Ready to Discover Buttons
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Start learning mode to discover new EnOcean buttons.
              Press any button on your EnOcean device to register it.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
              <Button
                variant="outlined"
                onClick={handleFullReset}
              >
                Change Hub
              </Button>
              <Button
                variant="contained"
                size="large"
                startIcon={loading ? <CircularProgress size={20} /> : <StartIcon />}
                onClick={handleStartLearning}
                disabled={loading}
              >
                Start Learning Mode
              </Button>
            </Box>
          </Paper>
        )}

        {/* Discovered Buttons List */}
        {selectedHub && (isLearning || discoveredButtons.length > 0) && (
          <Box sx={{ mb: 3 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="subtitle1" fontWeight="bold">
                Discovered Buttons ({discoveredButtons.length})
              </Typography>
              {isLearning && (
                <Button
                  size="small"
                  color="error"
                  startIcon={<StopIcon />}
                  onClick={handleStopLearning}
                  disabled={loading}
                >
                  Stop Learning
                </Button>
              )}
            </Box>

            {discoveredButtons.length === 0 && isLearning && (
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  textAlign: 'center',
                  bgcolor: 'action.hover',
                  borderRadius: 2
                }}
              >
                <CircularProgress size={40} sx={{ mb: 2 }} />
                <Typography variant="body1">
                  Waiting for button press...
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Press any EnOcean button to discover it
                </Typography>
              </Paper>
            )}

            <List>
              {discoveredButtons.map((button) => {
                const signalInfo = getSignalInfo(button.rssiDbm);
                const isSelected = selectedButton?.hexCode === button.hexCode;

                return (
                  <ListItem
                    key={button.hexCode}
                    button
                    selected={isSelected}
                    onClick={() => button.status === 'discovered' && handleSelectButton(button)}
                    sx={{
                      borderRadius: 1,
                      mb: 1,
                      border: 1,
                      borderColor: isSelected ? 'primary.main' : 'divider',
                      bgcolor: isSelected ? 'action.selected' : 'background.paper'
                    }}
                  >
                    <ListItemIcon>
                      {button.status === 'confirmed' ? (
                        <CheckIcon color="success" />
                      ) : button.status === 'awaiting_confirmation' ? (
                        <CircularProgress size={24} />
                      ) : (
                        <ButtonIcon color="primary" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="subtitle2" fontFamily="monospace">
                            {button.hexCode}
                          </Typography>
                          <Chip
                            size="small"
                            label={button.status}
                            color={
                              button.status === 'confirmed' ? 'success' :
                              button.status === 'awaiting_confirmation' ? 'warning' :
                              'default'
                            }
                          />
                        </Box>
                      }
                      secondary={
                        <Box display="flex" alignItems="center" gap={2}>
                          <Tooltip title={`Signal: ${signalInfo.label} (${button.rssiDbm} dBm)`}>
                            <Chip
                              size="small"
                              icon={<SignalIcon />}
                              label={signalInfo.label}
                              color={signalInfo.color}
                              variant="outlined"
                            />
                          </Tooltip>
                          {button.channel && (
                            <Typography variant="caption" color="text.secondary">
                              Channel: {button.channel}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      {button.status === 'discovered' && (
                        <IconButton
                          edge="end"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCancelDiscovery(button.id || button.discoveryId || button.hexCode);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </ListItemSecondaryAction>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        )}

        {/* Configuration Stepper */}
        {selectedHub && selectedButton && (
          <Box>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Configure Button: {selectedButton.hexCode}
            </Typography>

            <Stepper activeStep={activeStep} orientation="vertical">
              {/* Step 1: Select discovered button */}
              <Step completed={activeStep > 0}>
                <StepLabel>Select Button</StepLabel>
                <StepContent>
                  <Typography variant="body2" color="text.secondary">
                    Button {selectedButton.hexCode} selected
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => setActiveStep(1)}
                    sx={{ mt: 1 }}
                  >
                    Continue
                  </Button>
                </StepContent>
              </Step>

              {/* Step 2: Configure */}
              <Step completed={activeStep > 1}>
                <StepLabel>Configure</StepLabel>
                <StepContent>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    {/* Friendly Name */}
                    <TextField
                      label="Button Name"
                      value={friendlyName}
                      onChange={(e) => setFriendlyName(e.target.value)}
                      fullWidth
                      size="small"
                    />

                    {/* Button Type */}
                    <FormControl fullWidth size="small">
                      <InputLabel>Button Type</InputLabel>
                      <Select
                        value={buttonType}
                        onChange={(e) => setButtonType(e.target.value)}
                        label="Button Type"
                      >
                        {BUTTON_TYPES.map((type) => (
                          <MenuItem key={type.value} value={type.value}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Chip
                                size="small"
                                label={type.label}
                                color={type.color}
                              />
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {/* Assignment Type Selection - Fixed vs Mobile */}
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Assignment Type
                      </Typography>
                      <Box display="flex" gap={1}>
                        <Paper
                          elevation={assignmentType === 'fixed' ? 3 : 0}
                          onClick={() => {
                            setAssignmentType('fixed');
                            setSelectedPersonId(null);
                          }}
                          sx={{
                            flex: 1,
                            p: 2,
                            cursor: 'pointer',
                            border: 2,
                            borderColor: assignmentType === 'fixed' ? 'primary.main' : 'divider',
                            bgcolor: assignmentType === 'fixed' ? 'primary.light' : 'background.paper',
                            borderRadius: 2,
                            transition: 'all 0.2s',
                            '&:hover': {
                              borderColor: assignmentType === 'fixed' ? 'primary.main' : 'primary.light',
                              bgcolor: assignmentType === 'fixed' ? 'primary.light' : 'action.hover'
                            }
                          }}
                        >
                          <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                            <PlaceIcon
                              sx={{
                                fontSize: 40,
                                color: assignmentType === 'fixed' ? 'primary.contrastText' : 'primary.main'
                              }}
                            />
                            <Typography
                              variant="subtitle2"
                              fontWeight="bold"
                              color={assignmentType === 'fixed' ? 'primary.contrastText' : 'text.primary'}
                            >
                              Fixed
                            </Typography>
                            <Typography
                              variant="caption"
                              textAlign="center"
                              color={assignmentType === 'fixed' ? 'primary.contrastText' : 'text.secondary'}
                            >
                              Assign to a location
                            </Typography>
                          </Box>
                        </Paper>

                        <Paper
                          elevation={assignmentType === 'mobile' ? 3 : 0}
                          onClick={() => {
                            setAssignmentType('mobile');
                            setSelectedLocationId(null);
                          }}
                          sx={{
                            flex: 1,
                            p: 2,
                            cursor: 'pointer',
                            border: 2,
                            borderColor: assignmentType === 'mobile' ? 'secondary.main' : 'divider',
                            bgcolor: assignmentType === 'mobile' ? 'secondary.light' : 'background.paper',
                            borderRadius: 2,
                            transition: 'all 0.2s',
                            '&:hover': {
                              borderColor: assignmentType === 'mobile' ? 'secondary.main' : 'secondary.light',
                              bgcolor: assignmentType === 'mobile' ? 'secondary.light' : 'action.hover'
                            }
                          }}
                        >
                          <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                            <MobileIcon
                              sx={{
                                fontSize: 40,
                                color: assignmentType === 'mobile' ? 'secondary.contrastText' : 'secondary.main'
                              }}
                            />
                            <Typography
                              variant="subtitle2"
                              fontWeight="bold"
                              color={assignmentType === 'mobile' ? 'secondary.contrastText' : 'text.primary'}
                            >
                              Mobile
                            </Typography>
                            <Typography
                              variant="caption"
                              textAlign="center"
                              color={assignmentType === 'mobile' ? 'secondary.contrastText' : 'text.secondary'}
                            >
                              Assign to a person
                            </Typography>
                          </Box>
                        </Paper>
                      </Box>
                    </Box>

                    {/* Location Selection - only shown when Fixed is selected */}
                    <Collapse in={assignmentType === 'fixed'}>
                      <Box>
                        <Autocomplete
                          size="small"
                          options={enrichedLocations}
                          loading={loadingSiteData}
                          getOptionLabel={(option) => option.displayName || ''}
                          value={enrichedLocations.find(l => l.id === selectedLocationId) || null}
                          onChange={(e, newValue) => setSelectedLocationId(newValue?.id || null)}
                          filterOptions={(options, { inputValue }) => {
                            const searchTerm = inputValue.toLowerCase();
                            return options.filter(option =>
                              option.displayName?.toLowerCase().includes(searchTerm) ||
                              option.path?.toLowerCase().includes(searchTerm)
                            );
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Search Location"
                              placeholder={loadingSiteData ? 'Loading...' : 'Type to search...'}
                            />
                          )}
                          renderOption={(props, option) => (
                            <li {...props} key={option.id}>
                              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                <Typography variant="body2">{option.displayName}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {option.path}
                                </Typography>
                              </Box>
                            </li>
                          )}
                          ListboxProps={{ style: { maxHeight: 300 } }}
                          noOptionsText="No locations found in this site"
                          isOptionEqualToValue={(option, value) => option.id === value?.id}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                          {loadingSiteData ? 'Loading locations...' : `${enrichedLocations.length} location${enrichedLocations.length !== 1 ? 's' : ''} in this site`}
                        </Typography>
                        {assignmentType === 'fixed' && selectedLocationId && (
                          <Chip
                            icon={<PlaceIcon />}
                            label="Fixed Button"
                            color="primary"
                            size="small"
                            sx={{ mt: 1 }}
                          />
                        )}
                      </Box>
                    </Collapse>

                    {/* Person Selection - only shown when Mobile is selected */}
                    <Collapse in={assignmentType === 'mobile'}>
                      <Box>
                        <Autocomplete
                          size="small"
                          options={filteredPersons}
                          loading={loadingSiteData}
                          getOptionLabel={(option) => option.searchLabel || option.friendlyName || ''}
                          value={filteredPersons.find(p => p.id === selectedPersonId) || null}
                          onChange={(e, newValue) => setSelectedPersonId(newValue?.id || null)}
                          filterOptions={(options, { inputValue }) => {
                            const searchTerm = inputValue.toLowerCase();
                            return options.filter(option =>
                              option.friendlyName?.toLowerCase().includes(searchTerm) ||
                              option.locationName?.toLowerCase().includes(searchTerm)
                            );
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Search Person or Location"
                              placeholder={loadingSiteData ? 'Loading...' : 'Type to search by name or location...'}
                            />
                          )}
                          renderOption={(props, option) => (
                            <li {...props} key={option.id}>
                              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                <Typography variant="body2">{option.friendlyName}</Typography>
                                {option.locationName && (
                                  <Typography variant="caption" color="text.secondary">
                                    <LocationIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                                    {option.locationName}
                                  </Typography>
                                )}
                              </Box>
                            </li>
                          )}
                          ListboxProps={{ style: { maxHeight: 300 } }}
                          noOptionsText="No persons found in this site (only residents shown)"
                          isOptionEqualToValue={(option, value) => option.id === value?.id}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                          {loadingSiteData ? 'Loading persons...' : `${filteredPersons.length} person${filteredPersons.length !== 1 ? 's' : ''} in this site`}
                        </Typography>
                        {assignmentType === 'mobile' && selectedPersonId && !personButtonWarning && (
                          <Chip
                            icon={<MobileIcon />}
                            label="Mobile Button"
                            color="secondary"
                            size="small"
                            sx={{ mt: 1 }}
                          />
                        )}
                        {checkingPerson && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                            <CircularProgress size={16} />
                            <Typography variant="caption" color="text.secondary">
                              Checking existing buttons...
                            </Typography>
                          </Box>
                        )}
                        {personButtonWarning && (
                          <Alert severity="error" sx={{ mt: 1 }}>
                            <Typography variant="body2">
                              This person already has a mobile button assigned: <strong>{personButtonWarning.existingButton?.friendlyName || personButtonWarning.existingButton?.deviceKey}</strong>
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Each person can only have one mobile button. Please unlink the existing button first.
                            </Typography>
                          </Alert>
                        )}
                      </Box>
                    </Collapse>

                    <Box display="flex" gap={1}>
                      <Button
                        variant="outlined"
                        onClick={handleReset}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleConfigureButton}
                        disabled={
                          loading ||
                          checkingPerson ||
                          !assignmentType ||
                          (assignmentType === 'fixed' && !selectedLocationId) ||
                          (assignmentType === 'mobile' && !selectedPersonId) ||
                          (assignmentType === 'mobile' && personButtonWarning)
                        }
                        startIcon={loading ? <CircularProgress size={20} /> : null}
                      >
                        Save & Wait for Confirmation
                      </Button>
                    </Box>
                  </Box>
                </StepContent>
              </Step>

              {/* Step 3: Wait for confirmation */}
              <Step completed={activeStep > 2}>
                <StepLabel>Confirm Registration</StepLabel>
                <StepContent>
                  <Alert severity="info" icon={<TouchAppIcon />}>
                    Press the button <strong>{selectedButton.hexCode}</strong> again to confirm registration.
                  </Alert>
                  <Box sx={{ mt: 2 }}>
                    <CircularProgress size={24} sx={{ mr: 1 }} />
                    <Typography variant="body2" component="span" color="text.secondary">
                      Waiting for button press...
                    </Typography>
                  </Box>
                </StepContent>
              </Step>

              {/* Step 4: Complete */}
              <Step completed={activeStep >= 3}>
                <StepLabel>Complete</StepLabel>
                <StepContent>
                  <Alert severity="success" icon={<CheckIcon />}>
                    Button successfully registered!
                  </Alert>
                  <Button
                    variant="contained"
                    onClick={handleReset}
                    sx={{ mt: 2 }}
                  >
                    Register Another Button
                  </Button>
                </StepContent>
              </Step>
            </Stepper>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ButtonDiscoveryDialog;
